/* Peak Moto size calculator.
 *
 * A port of Kiwi Sizing's "Calculate my size" matching (their
 * getTableFieldStat / getCalculatorMatchMeasurements /
 * calculatePossibleSizeMatchResult, read from SizingPlugin.prod.js v341),
 * so the recommendation stays the same after the switchover. Used by the
 * theme (assets/pmo-size-calc.js) and by PMO's offline checks; keep the two
 * copies identical.
 *
 * Every chart field Peak Moto uses is Kiwi's "cover" type: the customer's
 * body measurement is compared with the top of each size's range, allowing
 * the field's stretch on top -- except a leg length (inseam / inside leg),
 * which is matched to the CLOSEST length, not the first one long enough:
 * a 77cm inseam is a Regular 76.7, not a Tall 86.4.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.PmoSizeCalc = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var TO_CM = { cm: 1, "cm-ft": 1, "ft-cm": 1, mm: 0.1, in: 2.54 };

  function p4(n) {
    return parseFloat(n.toPrecision(4));
  }
  function finite(n) {
    return typeof n === "number" && isFinite(n);
  }

  // A cell's numbers in cm, low to high: "90-96" -> [90, 96].
  function cellRange(cell) {
    var f = TO_CM[cell.u || cell.unit] || 1;
    return String(cell.v != null ? cell.v : cell.value)
      .replace(/,/g, ".")
      .split("-")
      .map(function (x) {
        return parseFloat(x) * f;
      })
      .filter(finite)
      .sort(function (a, b) {
        return a - b;
      });
  }

  function cellText(cell) {
    return String(cell ? (cell.v != null ? cell.v : cell.value) : "").trim();
  }

  // Kiwi's getTableFieldStat: the column's top values, the gaps between
  // them, and their average gap.
  function fieldStat(rows, col) {
    var ranges = [];
    var tops = [];
    for (var r = 1; r < rows.length; r++) {
      var range = cellRange(rows[r][col] || {});
      ranges.push(range);
      tops.push(range[range.length - 1]);
    }
    var seen = {};
    var unique = [];
    tops.forEach(function (t) {
      var k = t + "::" + typeof t;
      if (!(k in seen)) {
        seen[k] = 1;
        unique.push(t);
      }
    });
    unique.sort(function (a, b) {
      return a - b;
    });
    var diffs = [];
    for (var i = 0; i < unique.length; i++) if (unique[i + 1]) diffs.push(p4(unique[i + 1] - unique[i]));
    var avg = p4(diffs.reduce(function (a, b) {
      return a + b;
    }, 0) / (diffs.length || 1));
    return { ranges: ranges, diffs: diffs, avg: avg };
  }

  // A size's label, taking the cell above when Kiwi merged it.
  function sizeLabel(rows, r, col) {
    for (var i = r; i >= 1; i--) {
      var t = cellText(rows[i][col]);
      if (t) return t;
    }
    return "";
  }

  // Kiwi's fit percentage: how far the body is from the top of the size.
  // 0 is far too tight, 100 far too loose; Kiwi's green zone is 17-83.
  function fitPercentage(e, t, n, r) {
    return Math.round(fitPosition(e, t, n, r));
  }

  // The same, unrounded.
  function fitPosition(e, t, n, r) {
    var i = 0;
    if (e > 0 && t > 0) {
      if (e > t) i = 0;
      else if (e >= 0.75 * t) i = 15 - ((e - 0.75 * t) / (0.25 * t)) * 15;
      else i = 35 - (e / (0.75 * t)) * 18;
    } else if (e > 0 && t === 0) i = 0;
    else if (e >= -1 * n) i = 35 + ((-1 * e) / n) * 30;
    else if (e >= -1 * n - 1.2 * r) i = 65 + ((-1 * e - n) / (1.2 * r)) * 18;
    else if (e >= -1 * n - 2.5 * r) i = 85 + ((-1 * e - n - 1.2 * r) / (1.3 * r)) * 15;
    else i = 100;
    return i;
  }

  function fitScore(s, fit) {
    if (fit === "slim") return s < 30 ? 0.041 * Math.exp(0.26 * s) : s <= 40 ? 100 : 634.965 * Math.exp(-0.04621 * s);
    if (fit === "loose") return s < 58 ? 6.122 * Math.exp(0.048159 * s) : s <= 72 ? 100 : 2785.79 * Math.exp(-0.04621 * s);
    if (s < 35) return 0.74465 * Math.exp(0.14 * s);
    if (s <= 65) return 100;
    return 2015.8983 * Math.exp(-0.04621 * s);
  }

  var LEG_RE = /\b(inseam|inside leg|leg length|leg)\b/i;
  function isLength(f) {
    return !!(f && (f.closest || LEG_RE.test(String(f.label || ""))));
  }

  // A leg length: how far the leg is from the garment's length (0 inside its
  // range), on a scale of the chart's usual step between lengths. Short and
  // long count the same, so the nearest length always wins.
  function lengthFit(input, range, avg) {
    var lo = range[0], hi = range[range.length - 1];
    var d = input < lo ? input - lo : input > hi ? input - hi : 0;
    var step = avg > 0 ? avg : 5;
    return {
      pct: Math.max(0, Math.min(100, Math.round(50 - (d / step) * 30))),
      score: 100 * Math.exp(-2 * Math.pow(d / step, 2))
    };
  }

  function normalise(rows, direction) {
    if (direction !== "row") return rows;
    var out = [];
    rows.forEach(function (row, r) {
      row.forEach(function (cell, c) {
        (out[c] = out[c] || [])[r] = cell;
      });
    });
    return out;
  }

  /**
   * table: { rows: [[{v|value, u|unit, h|header}]], direction }
   * calc:  { size: sizeColumn, fields: [{ col, stretch, filterOversized }] }
   * inputs: body measurements in cm, one per field (same order)
   * fit:   "regular" (default), "slim" or "loose"
   * Returns { size, ranked: [...], all: [...] }; size is null when nothing fits.
   * Each entry's positions[] is where each measurement sits on the
   * tight (0) to loose (100) scale for that size.
   */
  function recommend(table, calc, inputs, fit) {
    // Kiwi works out the regular fit first; a fitted pick can't be bigger
    // than it and a relaxed pick can't be smaller.
    var regular = rank(table, calc, inputs, "regular");
    if (!fit || fit === "regular") return regular;
    var mine = rank(table, calc, inputs, fit);
    var anchor = regular.ranked.length ? regular.ranked[0].index : null;
    if (anchor != null) {
      mine.ranked = mine.ranked.filter(function (x) {
        return fit === "slim" ? x.index <= anchor : x.index >= anchor;
      });
      mine.size = mine.ranked.length ? mine.ranked[0].size : null;
    }
    return mine;
  }

  function rank(table, calc, inputs, fit) {
    var rows = normalise(table.rows, table.direction);
    var sizeCol = calc.size || 0;
    var fields = calc.fields.map(function (f, i) {
      var stat = fieldStat(rows, f.col);
      var measurements = [];
      for (var r = 1; r < rows.length; r++) {
        var range = stat.ranges[r - 1];
        var top = range[range.length - 1];
        var gap = stat.diffs[r - 2];
        if (range.length > 1) gap = range[1] - range[0];
        else if (!finite(gap)) gap = stat.avg;
        measurements.push({ index: r - 1, max: top, gap: gap, label: sizeLabel(rows, r, sizeCol) });
      }
      return { input: Number(inputs[i]), stretch: Number(f.stretch) || 0, filterOversized: !!f.filterOversized, avg: stat.avg, length: isLength(f), ranges: stat.ranges, measurements: measurements };
    });
    var all = fields[0].measurements.map(function (m0, idx) {
      var raw = 0;
      var maxScore = 0;
      var tooExtreme = false;
      var positions = [];
      fields.forEach(function (f) {
        var m = f.measurements[idx];
        if (f.length) {
          var lf = lengthFit(f.input, f.ranges[idx], f.avg);
          positions.push(lf.pct);
          raw += lf.score;
          maxScore += 100;
          return;
        }
        var diff = p4(f.input - m.max);
        var pos = fitPosition(diff, m.max * f.stretch, m.gap, f.avg);
        var pct = Math.round(pos);
        positions.push(pct);
        raw += fitScore(pct, fit);
        maxScore += 100;
        if (pct <= 10 || (f.filterOversized && pct >= 92.5)) tooExtreme = true;
      });
      return { index: idx, size: m0.label, positions: positions, rawFitScore: raw, fitScore: Math.round(Math.max(raw, 0)), scaledFitScore: Math.round((Math.max(raw, 0) / maxScore) * 100), tooExtreme: tooExtreme };
    });
    var ranked = all.slice().sort(function (a, b) {
      return b.rawFitScore - a.rawFitScore;
    }).filter(function (x) {
      return !x.tooExtreme;
    });
    return { size: ranked.length ? ranked[0].size : null, ranked: ranked, all: all };
  }

  // Covers: the first size whose every listed measurement is at least the
  // input plus calc.add (clearance). Returns { size, index } or size null.
  function coverSize(table, calc, inputs) {
    var rows = normalise(table.rows, table.direction);
    var add = Number(calc.add) || 0;
    for (var r = 1; r < rows.length; r++) {
      var ok = calc.fields.every(function (f, i) {
        var range = cellRange(rows[r][f.col] || {});
        return range.length && range[range.length - 1] >= Number(inputs[i]) + add;
      });
      if (ok) return { size: sizeLabel(rows, r, calc.size || 0), index: r - 1 };
    }
    return { size: null, index: null };
  }

  return { recommend: recommend, coverSize: coverSize, fitPercentage: fitPercentage, fitPosition: fitPosition, fitScore: fitScore, fieldStat: fieldStat };
});
